import { useCallback, useId } from "preact/hooks";
import { computed, signal } from "@preact/signals";

const pipWindow = signal<Window | null>(null);
const activeId = signal("");
const isSupported = "documentPictureInPicture" in window;

const requestPiP = async (id: string, width: number, height: number) => {
    if (!isSupported) {
        return null;
    }

    if (pipWindow.peek()) {
        activeId.value = id;

        return pipWindow.peek();
    }

    try {
        const pip = await window.documentPictureInPicture.requestWindow({
            width,
            height,
        });

        pip.addEventListener("pagehide", () => (pipWindow.value = null));

        Array.from(document.styleSheets).forEach((sheet) => {
            try {
                const rules = Array.from(sheet.cssRules)
                    .map((rule) => rule.cssText)
                    .join("");
                const style = document.createElement("style");

                style.textContent = rules;
                pip.document.head.appendChild(style);
            } catch (err: unknown) {
                if (sheet.href === null) {
                    return;
                }

                const link = document.createElement("link");

                link.rel = "stylesheet";
                link.type = sheet.type;
                link.media = sheet.media.toString();
                link.href = sheet.href;
                pip.document.head.appendChild(link);
            }
        });

        pipWindow.value = pip;
        activeId.value = id;
    } catch {
        return null;
    }

    return pipWindow.peek();
};

const close = () => {
    const pip = pipWindow.peek();

    if (pip) {
        pip.close();
        pipWindow.value = null;
    }
};

const useDocumentPiP = (id?: string) => {
    const ownId = useId();
    const finalId = id || ownId;
    const active = computed(
        () => activeId.value === finalId && pipWindow.value !== null,
    );

    const request = useCallback(
        (width: number, height: number) => requestPiP(finalId, width, height),
        [finalId],
    );

    return {
        id: finalId,
        pipWindow,
        active,
        activeId,
        isSupported,
        request,
        close,
    };
};

export default useDocumentPiP;
